import { useMemo } from "react";
import dayjs from "dayjs";
import { useQueries } from "react-query";
import { DateRange } from "@/components/shared/date-range-picker/DateRangePicker";

export function useTrend<Res>({
  dateRange,
  queryKey,
  queryFn,
  getParams,
  calculateTrend,
  numOfWindows = 1,
}: {
  dateRange: DateRange;
  queryKey: string[];
  queryFn: (params: string) => Promise<Res>;
  getParams: (window: DateRange) => string;
  calculateTrend: (
    results: { window: DateRange; response: Res }[]
  ) => number | undefined;
  numOfWindows?: number;
}) {
  const timeWindows = useMemo(() => {
    const startWindow = dateRange;
    const windowSize = dayjs(startWindow.dateTo).diff(
      dayjs(startWindow.dateFrom),
      "days"
    );

    let lastDateFrom = startWindow.dateFrom;

    const windows = [startWindow];
    for (let i = 0; i < numOfWindows; i++) {
      const newDateTo = lastDateFrom;
      const newDateFrom = dayjs(newDateTo)
        .subtract(windowSize, "days")
        .toISOString();

      windows.push({
        dateFrom: newDateFrom,
        dateTo: newDateTo,
      });

      lastDateFrom = newDateFrom;
    }

    return windows;
  }, [dateRange]);

  const queries = useQueries(
    timeWindows.map((window) => ({
      queryKey: ["trend", ...queryKey, window.dateFrom, window.dateTo],
      queryFn: async () => {
        const params = getParams(window);
        const response = await queryFn(params);

        return { window, response };
      },
    }))
  );

  return useMemo(() => {
    if (queries.some((query) => query.isLoading)) {
      return { trend: undefined, response: undefined };
    }

    const results: {
      window: DateRange;
      response: Res;
    }[] = [];

    queries.forEach((query) => {
      if (query.data != null) {
        results.push(query.data);
      }
    });

    const trend = calculateTrend(results);
    const response = results[0]?.response;

    return { trend, response };
  }, [queries]);
}
